import { getDatadogInstance } from '@cw-elements/analytics-provider';
import type React from 'react';
import { Component } from 'react';

import { useErrorBoundaryStore } from './error-boundary.store';
import { SomethingWentWrong } from './something-went-wrong.container';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export const APIErrorBoundary = ({ children }: ErrorBoundaryProps) => {
  const { APIError } = useErrorBoundaryStore();

  if (APIError) {
    getDatadogInstance()?.setGlobalContextProperty('error_type', 'APIError');

    throw APIError;
  }

  return <>{children}</>;
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ errorInfo, error });
    getDatadogInstance()?.setGlobalContextProperty('crash_screen', 'true');
  }

  render() {
    const { hasError, error, errorInfo } = this.state;

    if (hasError && error) {
      return (
        <SomethingWentWrong
          error={error}
          componentStack={errorInfo?.componentStack ?? ''}
        />
      );
    }

    return <APIErrorBoundary>{this.props.children}</APIErrorBoundary>;
  }
}

export default ErrorBoundary;
